import React, { useState } from "react";
import { FaTimesCircle } from "react-icons/fa";
import CopyToClipboard from "react-copy-to-clipboard";
import { formatAmount } from "../../utils/FormatPerson";
import { depositIcon, rewardIcon, swapIcon } from "../../assets/images";
import { IoArrowDownCircle, IoArrowUpCircle } from "react-icons/io5";

interface WalletActivityModalProps {
  transaction: any;
  setActivityModal: (value: boolean) => void;
}

const WalletActivityModal = ({
  transaction,
  setActivityModal,
}: WalletActivityModalProps) => {
  const [copyId, setCopyId] = useState<boolean>(false);

  // Handle copy to clipboard with timeout
  const handleCopy = () => {
    setCopyId(true);
    setTimeout(() => {
      setCopyId(false);
    }, 3000);
  };

  // Format date
  const formatDate = () => {
    const dateString = transaction?.createdAt || transaction?.processedAt;
    const date = new Date(dateString);

    const day = date.getDate();
    const ordinalSuffix = (day: number) => {
      switch (day) {
        case 1:
        case 21:
        case 31:
          return "st";
        case 2:
        case 22:
          return "nd";
        case 3:
        case 23:
          return "rd";
        default:
          return "th";
      }
    };

    return `${date.toLocaleString("default", {
      month: "short",
    })} ${day}${ordinalSuffix(
      day
    )}, ${date.getFullYear()} | ${date.toLocaleString("default", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })}`;
  };

  // Get transaction type display
  const getTransactionTypeDisplay = (type: string) => {
    switch (type) {
      case "deex-deposits":
        return "Deposit";
      case "deex-crypto-withdrawals":
        return "Withdrawal";
      case "deex-asset-swaps":
        return "Asset Swap";
      case "gift-card-transactions":
        return "Gift Card";
      case "payouts":
        return "Payout";
      case "crypto-deposit":
        return "Crypto Deposit";
      default:
        return type;
    }
  };

  // Format amount display
  const formatAmountDisplay = () => {
    if (transaction.transactionType === "deex-asset-swaps") {
      return `${formatAmount(transaction.from.amount)} ${
        transaction?.from?.asset
      }`;
    } else if (transaction.transactionType === "deex-deposits") {
      return formatAmount(transaction.amount);
    } else if (transaction.transactionType === "payouts") {
      return formatAmount(transaction.asset.amountInUSD);
    } else if (transaction.transactionType === "deex-fiat-deposits") {
      return "----";
    } else {
      return formatAmount(transaction.usdtAmount);
    }
  };

  // Get transaction icon
  const getTransactionIcon = () => {
    if (transaction.transactionType === "deex-asset-swaps") {
      return swapIcon;
    } else if (
      transaction.transactionType === "payouts" &&
      transaction.transaction?.type === "DEEX-POINT"
    ) {
      return rewardIcon;
    } else {
      return depositIcon;
    }
  };

  // Get transaction type label
  const getTransactionTypeLabel = () => {
    if (transaction.transactionType === "deex-asset-swaps") {
      return "Autoswap";
    } else if (
      transaction.transactionType === "payouts" &&
      transaction.transaction?.type === "DEEX-POINT"
    ) {
      return "Reward";
    } else if (transaction.transactionType === "payouts") {
      return "Payout";
    } else {
      return "Deposit";
    }
  };

  // Get provider name
  const getProviderName = () => {
    if (transaction?.provider?.name) {
      return transaction.provider.name;
    } else if (
      transaction.transactionType === "payouts" ||
      transaction.transactionType === "deex-fiat-deposits"
    ) {
      return "Hizo";
    } else if (transaction.transactionType === "deex-deposits") {
      return transaction.provider?.name || "Quidax";
    } else {
      return "Quidax";
    }
  };

  return (
    <div className="fixed inset-0 flex font-manrope justify-center items-start overflow-auto pt-16 pb-16 bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="w-11/12 mds:10/12 lgss:w-2/5 transition-all p-6 ease-in duration-1000 rounded-xl bg-[#0D0D0D]">
        <div className="flex pt-4 justify-between items-center">
          <h4 className="font-bold text-[17px] font-sora text-white">
            {getTransactionTypeDisplay(transaction?.transactionType)} Details
          </h4>
          <FaTimesCircle
            onClick={() => setActivityModal(false)}
            className="text-white text-[26px] cursor-pointer"
          />
        </div>

        <div className="w-full flex-col mt-12 justify-center items-center">
          <h4 className="text-[42px] text-[#DEE1E6] text-center font-bold font-sora">
            {transaction.transactionType === "payouts" ||
            transaction.transactionType === "deex-crypto-withdrawals"
              ? "-"
              : "+"}{" "}
            {formatAmountDisplay()}
          </h4>
          {transaction.transactionType === "deex-asset-swaps" && (
            <h4 className="text-[18px] capitalize text-[#DEE1E6] text-center font-bold font-sora">
              {transaction?.from?.asset} → {transaction?.to?.asset}
            </h4>
          )}
          <div className="flex justify-center items-center gap-2 mt-2">
            <span className="w-[14px] h-[14px]">
              <img
                src={getTransactionIcon()}
                className="w-full h-full bg-cover"
                alt=""
              />
            </span>
            <span className="text-[#869AA9] text-[14px]">
              {getTransactionTypeLabel()}
            </span>
          </div>
        </div>

        <div className="w-full h-[1px] bg-[#AEBCC6] mt-12 mb-4" />

        {/* Transaction Type */}
        <div className="w-full mt-2 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
            Transaction Type
          </h4>
          <h4 className="text-[#869AA9] text-[14px] capitalize">
            {transaction.transactionType === "payouts" ||
            transaction.transactionType === "deex-crypto-withdrawals"
              ? "Debit"
              : "Credit"}
          </h4>
        </div>

        {/* Provider */}
        <div className="w-full mt-2 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Provider</h4>
          <h4 className="text-[#869AA9] text-[14px] capitalize">
            {getProviderName()}
          </h4>
        </div>

        {/* Amount */}
        <div className="w-full mt-2 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Amount</h4>
          <h4 className="text-[#869AA9] text-[14px]">
            {transaction.transactionType === "deex-asset-swaps"
              ? `${formatAmount(transaction.from.amount)} ${
                  transaction?.from?.asset
                }`
              : transaction.transactionType === "deex-deposits"
              ? formatAmount(transaction.amount)
              : transaction.transactionType === "payouts"
              ? formatAmount(transaction.asset.amountInUSD)
              : transaction.transactionType === "deex-fiat-deposits"
              ? "----"
              : formatAmount(transaction.usdtAmount)}{" "}
            {transaction.transactionType === "gift-card-transactions"
              ? transaction.card?.name
              : transaction.transactionType === "payouts"
              ? transaction?.asset?.name
              : transaction.currency}
          </h4>
        </div>

        {/* Amount in NGN */}
        {transaction.transactionType === "payouts" && (
          <div className="w-full mt-2 flex justify-between items-center">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
              Amount (NGN)
            </h4>
            <h4 className="text-[#869AA9] text-[14px]">
              {transaction.payoutAmount?.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </h4>
          </div>
        )}

        {/* Transaction ID */}
        <div className="w-full flex mt-2 justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
            Transaction ID
          </h4>
          <CopyToClipboard text={transaction?._id || ""} onCopy={handleCopy}>
            {copyId ? (
              <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                Copied
              </h4>
            ) : (
              <h4 className="text-[#869AA9] text-[13px] cursor-pointer break-all">
                {transaction?._id}
              </h4>
            )}
          </CopyToClipboard>
        </div>

        {/* Status */}
        <div className="w-full mt-2 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Status</h4>
          {transaction.transactionType === "payouts" ? (
            <h4
              className={
                transaction.status === "PENDING"
                  ? "text-[#CB9200] flex justify-center items-center w-2/5 bg-[#181818] tracking-wider h-[26px] rounded-full text-[9px] font-bold"
                  : transaction.status === "FAILED"
                  ? "text-[#b14444] flex justify-center items-center w-2/5 bg-[#181818] tracking-wider h-[26px] rounded-full text-[9px] font-bold"
                  : transaction.status === "COMPLETED" ||
                    transaction.status === "SUCCESSFUL"
                  ? "text-[#03C668] flex justify-center items-center w-2/5 bg-[#0E2B1F] tracking-wider h-[26px] rounded-full text-[9px] font-bold"
                  : ""
              }
            >
              {transaction.status}
            </h4>
          ) : (
            <h4 className="text-[#03C668] flex justify-center items-center w-2/5 bg-[#013019] tracking-wider h-[26px] rounded-full text-[9px] font-bold">
              COMPLETED
            </h4>
          )}
        </div>

        {/* Date */}
        <div className="w-full mt-2 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Date</h4>
          <h4 className="text-[#869AA9] text-[14px]">{formatDate()}</h4>
        </div>

        {/* Asset Swap Details */}
        {transaction?.transactionType === "deex-asset-swaps" && (
          <>
            <div className="w-full flex mt-2 justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                From Asset
              </h4>
              <h4 className="text-[#869AA9] text-[14px] uppercase">
                {transaction?.from?.asset}
              </h4>
            </div>
            <div className="w-full flex mt-2 justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                To Asset
              </h4>
              <h4 className="text-[#869AA9] text-[14px] uppercase">
                {transaction?.to?.asset}
              </h4>
            </div>
          </>
        )}

        {/* Gift Card Details */}
        {transaction?.transactionType === "gift-card-transactions" && (
          <div className="w-full flex justify-between items-center">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
              Card Type
            </h4>
            <h4 className="text-[#869AA9] text-[14px] uppercase">
              {transaction?.card?.name}
            </h4>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletActivityModal;
